import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { NamedLink } from '../../components';
import { hideTidioChat, showTidioChat } from '../../util/tidioChat';
import css from './ListingPage.module.css';

const LoyaltyProgramPanel = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    hideTidioChat();
    document.body.style.overflow = 'hidden';
    const handleEscape = e => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      showTidioChat();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const steps = [
    {
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step1Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step1Description' }),
    },
    {
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step2Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step2Description' }),
    },
    {
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step3Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step3Description' }),
    },
  ];

  const faqItems = [
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq1Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq1Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq2Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq2Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq3Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq3Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq4Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq4Answer' }),
    },
  ];

  return ReactDOM.createPortal(
    <>
      <div className={css.loyaltyPanelOverlay} onClick={onClose} />
      <aside className={css.loyaltyPanel} aria-modal="true" role="dialog" aria-label="Programme fidélité">
        <header className={css.loyaltyPanelHeader}>
          <h2 className={css.loyaltyPanelTitle}>
            <FormattedMessage id="ProgrammeFidelitePage.heroBadge" />
          </h2>
          <button type="button" className={css.loyaltyPanelClose} onClick={onClose} aria-label="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </header>

        <div className={css.loyaltyPanelContent}>
          <section className={css.loyaltyPanelSection}>
            <h3 className={css.loyaltyPanelHeroTitle}>
              <FormattedMessage id="ProgrammeFidelitePage.heroTitle" />
            </h3>
            <p className={css.loyaltyPanelParagraph}>
              <FormattedMessage id="ProgrammeFidelitePage.heroSubtitle" />
            </p>
          </section>

          <section className={css.loyaltyPanelSection}>
            <h4 className={css.loyaltyPanelSectionTitle}>
              <FormattedMessage id="ProgrammeFidelitePage.howItWorksTitle" />
            </h4>
            <ol className={css.loyaltyPanelStepsList}>
              {steps.map(step => (
                <li key={step.title} className={css.loyaltyPanelStepItem}>
                  <strong>{step.title}.</strong> {step.description}
                </li>
              ))}
            </ol>
          </section>

          <section className={css.loyaltyPanelSection}>
            <h4 className={css.loyaltyPanelSectionTitle}>
              <FormattedMessage id="ProgrammeFidelitePage.faqTitle" />
            </h4>
            <div className={css.loyaltyPanelFaqList}>
              {faqItems.map(item => (
                <div key={item.question} className={css.loyaltyPanelFaqItem}>
                  <p className={css.loyaltyPanelFaqQuestion}>{item.question}</p>
                  <p className={css.loyaltyPanelFaqAnswer}>{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <NamedLink
            name="ProgrammeFidelitePage"
            className={css.loyaltyPanelPrimaryCta}
            onClick={onClose}
          >
            En savoir plus sur notre programme fidélité
          </NamedLink>
        </div>
      </aside>
    </>,
    document.body
  );
};

export default LoyaltyProgramPanel;
